import { Link } from 'react-router-dom';
import { Shield, Lock, CreditCard, Database, UserCheck, Mail, GraduationCap } from 'lucide-react';
import SEO from '../components/shared/SEO';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="privacy-policy-page">
      <SEO 
        title="Privacy Policy | Queen B Logistics"
        description="Privacy Policy for Queen B Holdings LLC, DBA Queen B Logistics - Learn how we collect, use, and protect your information."
      />
      
      {/* Header */}
      <section className="py-16 bg-[#050505] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/60">
            Queen B Holdings LLC, DBA Queen B Logistics<br />
            <span className="text-white/40">Last Updated: February 2026</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            
            {/* Section 1 */}
            <PolicySection number="1" title="Introduction">
              <p>
                Queen B Holdings LLC, DBA Queen B Logistics ("Company," "we," "our," or "us") operates a structured freight operations platform built around the <strong className="text-[#D4AF37]">Build • Protect • Grow</strong> lifecycle model.
              </p>
              <p>This Privacy Policy explains how we collect, use, store, and safeguard information when you:</p>
              <ul>
                <li>Visit our website</li>
                <li>Apply for services</li>
                <li>Use our Hive Carrier Portal</li>
                <li>Purchase Signature Program, Compliance, or Dispatch services</li>
                <li>Purchase or access Queen B Academy courses</li>
              </ul>
            </PolicySection>

            {/* Section 2 */}
            <PolicySection number="2" title="Information We Collect">
              <h4 className="text-white font-bold mt-6 mb-3">A. Information You Provide</h4>
              <ul>
                <li>Full name</li>
                <li>Company name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Business formation details</li>
                <li>MC/DOT number</li>
                <li>Insurance details</li>
                <li>Billing information</li>
                <li>Documents uploaded in Hive</li>
              </ul>

              <h4 className="text-white font-bold mt-6 mb-3">B. Automatically Collected Data</h4>
              <ul>
                <li>IP address</li>
                <li>Browser/device information</li>
                <li>Login timestamps</li>
                <li>Portal activity logs</li>
              </ul>
            </PolicySection>

            {/* Section 3 */}
            <PolicySection number="3" title="How We Use Your Information">
              <p>We use information to:</p>
              <ul>
                <li>Process Signature Program applications</li>
                <li>Provide compliance monitoring</li>
                <li>Provide dispatch services</li>
                <li>Verify authority status</li>
                <li>Manage subscription plans</li>
                <li>Process Stripe payments</li>
                <li>Deliver Queen B Academy course access and track progress</li>
                <li>Communicate updates</li>
                <li>Improve platform functionality</li>
              </ul>
              <div className="bg-green-500/10 border border-green-500/30 p-4 mt-4">
                <p className="text-green-400 font-bold m-0">We do NOT sell your data.</p>
              </div>
            </PolicySection>

            {/* Section 4 */}
            <PolicySection number="4" title="Queen B Academy" icon={GraduationCap}>
              <p>When you purchase or access Queen B Academy courses, we collect:</p>
              <ul>
                <li>Course enrollment and purchase history</li>
                <li>Quiz scores and progress data</li>
                <li>Lesson completion status</li>
                <li>Access code redemption records</li>
              </ul>
              <p>This data is used to:</p>
              <ul>
                <li>Deliver course content and track your learning progress</li>
                <li>Issue certificates upon course completion</li>
                <li>Improve course materials based on aggregate usage patterns</li>
              </ul>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 mt-4">
                <p className="text-[#D4AF37] m-0">Academy purchase and progress data is stored securely and not shared with third parties.</p>
              </div>
            </PolicySection>

            {/* Section 5 */}
            <PolicySection number="5" title="Payment Processing" icon={CreditCard}>
              <p>All payments are securely processed through <strong>Stripe</strong>.</p>
              <p>We do not store full credit card numbers.</p>
              <p>Recurring subscriptions are only initiated after explicit user confirmation.</p>
            </PolicySection>

            {/* Section 6 */}
            <PolicySection number="6" title="Data Protection" icon={Shield}>
              <p>We implement reasonable administrative and technical safeguards including:</p>
              <ul>
                <li>Secure hosting</li>
                <li>Encrypted data transmission</li>
                <li>Role-based portal access</li>
                <li>Restricted admin controls</li>
              </ul>
              <p className="text-white/50 italic">However, no system is 100% secure.</p>
            </PolicySection>

            {/* Section 7 */}
            <PolicySection number="7" title="Data Retention" icon={Database}>
              <p>We retain data:</p>
              <ul>
                <li>For active clients</li>
                <li>As required by law</li>
                <li>For internal record-keeping</li>
              </ul>
              <p>You may request data deletion unless legally required to retain it.</p>
            </PolicySection>

            {/* Section 8 */}
            <PolicySection number="8" title="Your Rights" icon={UserCheck}>
              <p>You may:</p>
              <ul>
                <li>Request access to your data</li>
                <li>Request corrections</li>
                <li>Request deletion (where applicable)</li>
              </ul>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 mt-4 flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                <p className="text-white m-0">Contact: <a href="mailto:support@queenblogistics.com" className="text-[#D4AF37] hover:underline">support@queenblogistics.com</a></p>
              </div>
            </PolicySection>

          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="text-[#D4AF37] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const PolicySection = ({ number, title, icon: Icon, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold text-sm">
        {number}
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase m-0">
        {title}
      </h2>
      {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
    </div>
    <div className="text-white/70 leading-relaxed pl-11">
      {children}
    </div>
  </div>
);

export default PrivacyPolicyPage;
